import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_DESTINATION } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputText from "@/design/forms/InputText";
import { any, number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viLocationPinDot } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, InputAdornment, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import destinationReducer from "./destinationReducer";

function Destination() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const phoneNumberStore = useSelector((state) => state.domain.phoneNumbers);
  const inboundRouteStore = useSelector((state) => state.domain.inboundRoutes);

  const phoneNumberList = useMemo(() => {
    return (
      phoneNumberStore.list?.map((phoneNumber) => ({
        did: phoneNumber.did,
        name: inboundRouteStore.list.find((inbound) => inbound.did === phoneNumber.did)?.name || "",
      })) || []
    );
  }, [inboundRouteStore.list, phoneNumberStore.list]);

  // Get general data
  useEffect(() => {
    Promise.resolve([Api.getPbx(), Api.getCurrentPhoneNumbers(), Api.getInbounds()]);
  }, []);

  const {
    value: destination,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_DESTINATION,
    reducer: destinationReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      destination: any()
        .required({ message: "Destination is required." })
        .truthy(
          (() => {
            const rawValue = destination.destination;
            const value = String(rawValue ?? "").trim();

            if (!value) return false;
            if (!isNaN(Number(value))) return Number(value) !== 0;

            return /^sips?:[^@\s]+@[^@\s]+$/i.test(value);
          })(),
          { message: "Destination must be a number or a valid SIP URI." },
        ),
      ringSeconds: number()
        .required({ message: "Ring seconds is required." })
        .min(1, { message: "Ring seconds must be between 1 and 120 seconds." })
        .max(120, {
          message: "Ring seconds must be between 1 and 120 seconds.",
        }),
    }),
    onSubmit: () => Api.storeDestination(destination),
    onSuccess: (response) => {
      if (!destination.id) {
        dispatch({ type: "id", value: response.data.customDestination.id });
        navigate(`../${response.data.customDestination.id}`);
      }

      Promise.resolve(Api.getDestinations());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === destination.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_DESTINATION });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getDestination({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.customDestination }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [destination.id, dispatch, id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getDestinations()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${destination.name || "Custom destination"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={destination.name} icon={viLocationPinDot} />
              </IconButton>
            ) : (
              <Avatar text={destination.name} icon={viLocationPinDot} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={destination.id || "nk"}
                isActionable={!!destination?.id}
                onDeleteCheck={() => Api.deleteDestination(destination, false)}
                onDelete={() => Api.deleteDestination(destination, true)}
                name={`${destination.name} (${destination.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={destination.name || "Unnamed custom destination"}
          subheader={destination.exten || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={destination.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="exten"
              label="Number"
              onChange={formHandler}
              value={destination.exten}
              helperText="Number cannot be 0 or any Belgian emergency number."
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Settings">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required
              id="destination"
              label="Destination"
              onChange={formHandler}
              value={destination.destination}
              helperText="Destination must be a number or a valid SIP URI."
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumeric
              required
              id="ringSeconds"
              label="Ring seconds"
              onChange={formHandler}
              value={destination.ringSeconds}
              min={1}
              max={120}
              endAdornment={
                <InputAdornment position="end" sx={{ marginRight: 1 }}>
                  seconds
                </InputAdornment>
              }
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputAutocomplete
              id="forceCallerId"
              label="Force Caller ID"
              onChange={formHandler}
              value={destination.forceCallerId}
              selectValue="did"
              selects={phoneNumberList}
              hasEmpty={null}
              getOptionLabel={(option) => (option.name ? `${option.did} \u00b7 ${option.name}` : option.did)}
              renderOptionLabel={(option) => (
                <>
                  {option.did}
                  {option.name !== "" && <span style={{ opacity: 0.6 }}>&nbsp;&middot; {option.name}</span>}
                </>
              )}
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Overflow">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <OverflowDrawer
              label="Overflow"
              value={destination.overflow}
              propertyName="overflow"
              overflowHandler={(value) => formHandler("overflow", value)}
              filter={{ exten: [destination.exten], custom: [], sound: [] }}
              helperText="Set the destination if the custom destination does not pick up the call."
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default Destination;
